import { Button, Group, Modal, PasswordInput, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../api/client";

/** Own password change (any user); other sessions of the user are closed by the API. */
export function PasswordModal({ opened, onClose }: { opened: boolean; onClose(): void }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const mismatch = repeat.length > 0 && next !== repeat;
  const reset = () => {
    setCurrent("");
    setNext("");
    setRepeat("");
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api("/api/auth/password", {
        method: "POST",
        body: { currentPassword: current, newPassword: next },
      });
      notifications.show({ message: t("password.changed"), color: "green" });
      reset();
      onClose();
    } catch (e) {
      const msg =
        e instanceof ApiError && e.status === 401
          ? t("password.wrongCurrent")
          : (e as Error).message;
      notifications.show({ message: msg, color: "red" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      title={t("password.title")}
    >
      <Stack>
        <PasswordInput
          label={t("password.current")}
          value={current}
          onChange={(e) => setCurrent(e.currentTarget.value)}
          autoComplete="current-password"
        />
        <PasswordInput
          label={t("password.new")}
          value={next}
          onChange={(e) => setNext(e.currentTarget.value)}
          description={t("password.rule")}
          autoComplete="new-password"
        />
        <PasswordInput
          label={t("password.repeat")}
          value={repeat}
          onChange={(e) => setRepeat(e.currentTarget.value)}
          error={mismatch ? t("password.mismatch") : undefined}
          autoComplete="new-password"
        />
        <Text size="xs" c="dimmed">
          {t("password.sessionsHint")}
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={submit}
            loading={busy}
            disabled={!current || next.length < 8 || next !== repeat}
          >
            {t("common.save")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
